import {useEffect, useCallback, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {updateFilterAtIndex, selectFilters, selectFilterDetailsLoading} from '../slices/breadCrumbsSlice'
import {usePointOfInterest} from './pointOfInterest'
import {useConfig} from './config'
import {useFilterListDetail} from './filterListDetail'

export const useStatesAndHealthConditions = () => {
  const filters = useSelector(selectFilters)
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)

  return useMemo(() => ({filters, filterDetailsLoading}), [filters, filterDetailsLoading])
}

export const useInitializeFilters = () => {
  const dispatch = useDispatch()
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)

  const {statesConfig} = useConfig() || {}
  const {states} = useFilterListDetail()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filterDetailsLoading && Object.keys(statesConfig || {}).length > 0 && states?.stateList?.length > 0) {
      const statesFilter = {...statesConfig}
      if (states?.stateList) {
        const statesLength = states.stateList.length
        if (statesLength === statesFilter.totalStates) {
          statesFilter.values = [
            {STATE_ID: 0, STATE_NAME: statesConfig.entireUnitedStateLabel, IS_ACTIVE: true},
            ...states.stateList
          ]
        } else {
          statesFilter.values = [...states.stateList]
        }
      } else {
        statesFilter.values = []
      }
      statesFilter.default = statesFilter.values.length > 0 ? [statesFilter.values[0].STATE_NAME] : ['No Data']

      // Dispatch serializable data
      dispatch(updateFilterAtIndex({index: 0, data: statesFilter}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDetailsLoading])
}

export const useUpdateDefaultContext = () => {
  const dispatch = useDispatch()

  const {statesConfig, defaultPOIConfig: poiConfig} = useConfig() || {}
  const {poi} = usePointOfInterest()

  const validation = useCallback(
    (newState) => {
      return (
        (newState.includes(statesConfig?.entireUnitedStateLabel) &&
          poi?.default?.length < poiConfig?.maxPOILengthNationView + 1) ||
        (newState.length === 1 &&
          !newState.includes(statesConfig?.entireUnitedStateLabel) &&
          poi?.default?.length < poiConfig?.maxPOILengthSinglState + 1) ||
        (newState.length === 2 && poi?.default?.length < poiConfig?.maxPOILengthMultiState + 1)
      )
    },
    [statesConfig, poi, poiConfig]
  )

  const updateDefaultState = useCallback(
    (selectedState, getAllStates, stateAbbreviation) => {
      const newState = selectedState
      if (poi && validation(newState)) {
        // Create updated states array without mutating the input
        let updatedStates
        if (selectedState.length === 2) {
          updatedStates = getAllStates.map((obj) => ({
            ...obj,
            IS_ACTIVE: newState.includes(obj.STATE_NAME) ? obj.IS_ACTIVE : false
          }))
        } else {
          updatedStates = getAllStates.map((event) => ({
            ...event,
            IS_ACTIVE: true
          }))
        }

        // Dispatch serializable data
        dispatch(
          updateFilterAtIndex({
            index: 0,
            data: {
              values: updatedStates,
              default: newState,
              stateAbbreviation
            }
          })
        )
      }
    },
    [poi, validation, dispatch]
  )

  return useMemo(() => ({updateDefaultState}), [updateDefaultState])
}
